/*
 * Cookie consent state.
 *
 * The rules this has to satisfy differ by jurisdiction, and the Cookie Policy spells them out:
 *
 *   EU  — non-essential cookies must not load before consent; consent must be granular,
 *         unambiguous and as easy to refuse as to give (so Reject All is a peer of Accept All,
 *         not a link buried in a preference screen); pre-ticked boxes are prohibited.
 *   UK  — analytics and functional cookies may run without prior consent, but only with a
 *         transparent and effective opt-out.
 *   US  — California and similar states require an opt-out of "sale"/"sharing", and require the
 *         Global Privacy Control browser signal to be honoured.
 *
 * Rather than detect the visitor's jurisdiction — which needs geolocation we do not have client
 * side, and which gets it wrong for travellers and VPN users — this applies the strictest of the
 * three to everyone: nothing non-essential runs until it is switched on. That is compliant
 * everywhere and is one code path instead of three.
 *
 * Stored under a version so that a materially changed cookie set re-prompts, which the Policy
 * requires ("and when the essential terms change").
 */

export const CONSENT_KEY = 'cookie-consent';
export const CONSENT_VERSION = 1;

/* Strictly necessary is absent on purpose: it is not a choice, and offering it as one implies it
   can be switched off. It is described on the cookie page instead. */
export const CATEGORIES = ['functional', 'analytics', 'marketing'];

const DENY_ALL = { functional: false, analytics: false, marketing: false };
const ALLOW_ALL = { functional: true, analytics: true, marketing: true };

/*
 * Global Privacy Control — a browser-level "do not sell or share" signal.
 *
 * Honouring it is mandatory in California and it legally counts as an opt-out request, so it wins
 * over anything stored locally: a visitor who set GPC has already answered, and asking again with
 * a banner would be asking them to re-consent to what they globally refused.
 */
export const hasGlobalPrivacyControl = () =>
  typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;

const read = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    /* Private mode, disabled storage, or hand-edited junk — treat as no decision rather than
       throwing on every page load. */
    return null;
  }
};

/* The stored decision, or null when the visitor has not made one yet. */
export const getStoredConsent = () => {
  const stored = read();
  if (!stored) return null;
  return { ...DENY_ALL, ...stored.categories };
};

/*
 * What is actually permitted right now.
 *
 * Never returns null: with no decision recorded, nothing non-essential is permitted, which is what
 * keeps analytics off before the banner is answered.
 */
export const getConsent = () => {
  if (hasGlobalPrivacyControl()) {
    /* GPC forbids sale/sharing. Functional cookies are neither, and refusing them would degrade
       the site for no privacy gain, so they stay available while analytics and marketing do not. */
    return { ...DENY_ALL, functional: true, gpc: true };
  }
  return { ...(getStoredConsent() || DENY_ALL), gpc: false };
};

export const allows = (category) => getConsent()[category] === true;

/* True when the banner still has to be shown. A GPC signal counts as an answer already given. */
export const needsConsentDecision = () => !hasGlobalPrivacyControl() && read() === null;

export const saveConsent = (categories) => {
  const value = { ...DENY_ALL, ...categories };
  /* Tells the pre-React inline script in index.html not to reserve space for the banner on the
     next load. Kept in sync with the stored decision — see openCookieSettings, which clears it. */
  try { document.documentElement.classList.add('cookie-accepted'); } catch { /* no DOM */ }
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      version: CONSENT_VERSION,
      categories: value,
      /* Kept because a consent record has to be able to show what was agreed and when */
      savedAt: new Date().toISOString(),
    }));
  } catch {
    /* Storage unavailable — the choice applies to this page view and is asked again next time,
       which is the safe direction to fail in. */
  }
  notify(value);
  return value;
};

export const acceptAll = () => saveConsent(ALLOW_ALL);
export const rejectAll = () => saveConsent(DENY_ALL);

/* Subscribers (the banner, the preference centre, anything that loads a tag) */
const listeners = new Set();

const notify = (value) => listeners.forEach((fn) => {
  try { fn(value); } catch { /* a broken listener must not block the others */ }
});

export const onConsentChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/*
 * Reopening the preference centre.
 *
 * The banner's visibility is state in AppContent; the "Cookie settings" link is in the footer,
 * several levels away, and this app has no context or store to share state through. A window event
 * is the least invasive connection between the two.
 */
export const COOKIE_SETTINGS_EVENT = 'plotex:open-cookie-settings';

/*
 * index.html marks <html class="cookie-accepted"> before React boots so an already-consented
 * visitor never sees the banner flash, and CookieBanner.css hides .cb-banner under that class with
 * !important. That is exactly what has to be undone here: without it the banner would mount in the
 * DOM and stay invisible, so the footer link would do nothing at all.
 */
export const openCookieSettings = () => {
  try { document.documentElement.classList.remove('cookie-accepted'); } catch { /* no DOM */ }
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
};
